// Kişisel bilgi formunun saf kuralları: taslaktan yazma gövdesi kurmak.
//
// Karar noktaları (kod yazılmadan ÖNCE listelendi, testler bunları ölçer):
//   1. Boş adres, kişinin adresi YOKKEN bir "sil" yazısı olmamalı; adresi VARKEN
//      ise `null` olmalı. Aksi hâlde ya sessiz bir no-op ya da yanlışlıkla silme.
//   2. Önden dolu gelen ülke ("TR") başka her alanı boş bir adresi "dolu" saymamalı:
//      yoksa hiçbir adres kutusu boşaltılamazdı.
//   3. Yarım doldurulmuş adres burada reddedilir, sunucunun 400'üne bırakılmaz.
//   4. Eklenip hiç yazılmamış bir kişi satırı hata değil, atılır; yarım doldurulmuş
//      satır ise reddedilir.
//   5. Yalnız boşluk boştur; ülke kodu büyük harfe çevrilir.
//   6. Telefon sayı gibi durmuyorsa reddedilir; boş telefon, yalnız kayıtlı bir
//      telefon VARKEN `null` yazar.
//   7. Hiçbir şey değişmediyse gövde boştur: uç "en az bir alan" ister ve boş
//      gövdeyi 400 ile reddeder, o yüzden çağıran boş gövdeyi hiç göndermemeli.
//
// Doğrulama mesajları çağıranın çeviri işleviyle çözülür (bu modül React dışında,
// ADR-006). Anahtarlar aşağıda düz çağrı olarak yazılı ki `i18n:extract` onları
// iki dil paketine karşı ölçebilsin.

import type { EmergencyContact, PersonalInfo, PersonalInfoPatch } from "./personal-info-api";
import { MAX_EMERGENCY_CONTACTS } from "./personal-info-api";

export interface AddressDraft {
  readonly line1: string;
  readonly line2: string;
  readonly city: string;
  readonly state_province: string;
  readonly postal_code: string;
  readonly country: string;
}

export interface ContactDraft {
  readonly name: string;
  readonly relationship: string;
  readonly phone: string;
  readonly email: string;
}

export interface PersonalInfoDraft {
  readonly phone: string;
  readonly address: AddressDraft;
  readonly contacts: readonly ContactDraft[];
}

/**
 * Boş formun ülkesi. Ürün Türkiye öncelikli ve çalışanların büyük çoğunluğu
 * burada oturuyor; her seferinde iki harf yazdırmak sürtünme, yanlış ülke ise
 * kullanıcının gözünün önünde duran, değiştirilebilir bir varsayılan.
 */
export const DEFAULT_COUNTRY = "TR";

export function emptyContact(): ContactDraft {
  return { name: "", relationship: "", phone: "", email: "" };
}

export function draftFromInfo(info: PersonalInfo): PersonalInfoDraft {
  const a = info.home_address;
  return {
    phone: info.primary_phone ?? "",
    address:
      a === null
        ? {
            line1: "",
            line2: "",
            city: "",
            state_province: "",
            postal_code: "",
            country: DEFAULT_COUNTRY,
          }
        : {
            line1: a.line1,
            line2: a.line2 ?? "",
            city: a.city,
            state_province: a.state_province ?? "",
            postal_code: a.postal_code ?? "",
            country: a.country,
          },
    contacts: info.emergency_contacts.map((c) => ({
      name: c.name,
      relationship: c.relationship,
      phone: c.phone,
      email: c.email ?? "",
    })),
  };
}

// Sunucudaki PhoneSchema ile aynı kural (routes/employee-personal-info.ts).
function isPhone(value: string): boolean {
  const digits = value.replace(/\D/g, "").length;
  return /^\+?[0-9()\-.\s]+$/.test(value) && digits >= 7 && digits <= 15;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type PatchAddress = NonNullable<PersonalInfoPatch["home_address"]>;
type PatchContact = NonNullable<PersonalInfoPatch["emergency_contacts"]>[number];

function sameAddress(current: PersonalInfo["home_address"], next: PatchAddress | null): boolean {
  if (current === null || next === null) return current === null && next === null;
  return (
    current.line1 === next.line1 &&
    (current.line2 ?? "") === (next.line2 ?? "") &&
    current.city === next.city &&
    (current.state_province ?? "") === (next.state_province ?? "") &&
    (current.postal_code ?? "") === (next.postal_code ?? "") &&
    current.country === next.country
  );
}

function sameContacts(
  current: readonly EmergencyContact[],
  next: readonly PatchContact[],
): boolean {
  return (
    current.length === next.length &&
    current.every((c, i) => {
      const n = next[i];
      return (
        n !== undefined &&
        c.name === n.name &&
        c.relationship === n.relationship &&
        c.phone === n.phone &&
        (c.email ?? "") === (n.email ?? "")
      );
    })
  );
}

export type BuildResult =
  | { readonly ok: true; readonly patch: PersonalInfoPatch }
  | { readonly ok: false; readonly errors: Readonly<Record<string, string>> };

/**
 * Taslağı, yalnız DEĞİŞEN gruplardan oluşan bir yazma gövdesine çevirir.
 * `errors` anahtarları alan yollarıdır: "phone", "address.city",
 * "contacts.2.phone".
 */
export function buildPatch(
  draft: PersonalInfoDraft,
  current: PersonalInfo,
  t: (key: string) => string,
): BuildResult {
  const errors: Record<string, string> = {};
  const patch: {
    primary_phone?: string | null;
    home_address?: PatchAddress | null;
    emergency_contacts?: PatchContact[];
  } = {};

  const phone = draft.phone.trim();
  if (phone !== "" && !isPhone(phone)) {
    errors["phone"] = t("people.personal.error.phone_invalid");
  } else if (phone !== (current.primary_phone ?? "")) {
    patch.primary_phone = phone === "" ? null : phone;
  }

  const a = draft.address;
  const addressBlank = [a.line1, a.line2, a.city, a.state_province, a.postal_code].every(
    (v) => v.trim() === "",
  );
  let address: PatchAddress | null = null;
  if (!addressBlank) {
    const country = a.country.trim().toUpperCase();
    if (a.line1.trim() === "") errors["address.line1"] = t("people.personal.error.line_one_required");
    if (a.city.trim() === "") errors["address.city"] = t("people.personal.error.city_required");
    if (!/^[A-Z]{2}$/.test(country)) {
      errors["address.country"] = t("people.personal.error.country_invalid");
    }
    address = {
      line1: a.line1.trim(),
      city: a.city.trim(),
      country,
      ...(a.line2.trim() !== "" && { line2: a.line2.trim() }),
      ...(a.state_province.trim() !== "" && { state_province: a.state_province.trim() }),
      ...(a.postal_code.trim() !== "" && { postal_code: a.postal_code.trim() }),
    };
  }
  if (!sameAddress(current.home_address, address)) patch.home_address = address;

  const contacts: PatchContact[] = [];
  draft.contacts.forEach((c, i) => {
    const name = c.name.trim();
    const relationship = c.relationship.trim();
    const contactPhone = c.phone.trim();
    const email = c.email.trim();
    if (name === "" && relationship === "" && contactPhone === "" && email === "") return;
    if (name === "") errors[`contacts.${i}.name`] = t("people.personal.error.contact_name_required");
    if (relationship === "") {
      errors[`contacts.${i}.relationship`] = t("people.personal.error.contact_relationship_required");
    }
    if (!isPhone(contactPhone)) {
      errors[`contacts.${i}.phone`] = t("people.personal.error.contact_phone_invalid");
    }
    if (email !== "" && !EMAIL.test(email)) {
      errors[`contacts.${i}.email`] = t("people.personal.error.contact_email_invalid");
    }
    contacts.push({ name, relationship, phone: contactPhone, ...(email !== "" && { email }) });
  });
  if (contacts.length > MAX_EMERGENCY_CONTACTS) {
    errors["contacts"] = t("people.personal.error.contacts_too_many");
  }
  if (!sameContacts(current.emergency_contacts, contacts)) patch.emergency_contacts = contacts;

  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, patch };
}
